import { Component, type ErrorInfo, type ReactNode } from 'react';

import { logger } from '../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error({ component: 'ErrorBoundary' }, 'Error caught by boundary', error, {
      errorInfo: errorInfo.componentStack,
    });
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  public override render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex items-center justify-center p-4 bg-error/10 dark:bg-error/10 rounded-lg">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-error dark:text-error">
                Something went wrong
              </h2>
              <p className="mt-2 text-sm text-error dark:text-error">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                type="button"
                onClick={this.handleRetry}
                className="mt-4 px-4 py-2 bg-error/10 dark:bg-error/10 text-error dark:text-error rounded hover:bg-error/20 dark:hover:bg-error/10 transition-colors"
              >
                Try again
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
